import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, CircularProgress, Divider, Avatar } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import { dmsApi } from '../services/dmsApi';

const ACTION_CONFIG = {
  upload: { icon: <UploadFileIcon />, color: '#4caf50', label: 'uploaded' },
  delete: { icon: <DeleteIcon />, color: '#f44336', label: 'deleted' },
  edit: { icon: <EditIcon />, color: '#ff9800', label: 'edited' },
  view: { icon: <VisibilityIcon />, color: '#2196f3', label: 'viewed' },
  download: { icon: <DownloadIcon />, color: '#9c27b0', label: 'downloaded' },
  share: { icon: <ShareIcon />, color: '#00bcd4', label: 'shared' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

const ActivityFeed = ({ maxItems = 8 }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadActivity = useCallback(async () => {
    try {
      const res = await dmsApi.getAuditLogs({ limit: maxItems });
      setActivities(res.data.logs || res.data || []);
    } catch {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [maxItems]);

  useEffect(() => {
    loadActivity();
    const interval = setInterval(loadActivity, 30000);
    return () => clearInterval(interval);
  }, [loadActivity]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;

  if (activities.length === 0) return (
    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No recent activity</Typography>
  );

  return (
    <List disablePadding dense>
      {activities.slice(0, maxItems).map((activity, idx) => {
        const actionConfig = ACTION_CONFIG[activity.action] || ACTION_CONFIG.view;
        const docTitle = activity.documentId?.title || 'a document';
        const userName = activity.userId?.name || 'Someone';
        return (
          <React.Fragment key={activity._id || idx}>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: `${actionConfig.color}20`, color: actionConfig.color }}>
                  {React.cloneElement(actionConfig.icon, { sx: { fontSize: 14 } })}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                    <strong>{userName}</strong> {actionConfig.label} <strong>{docTitle}</strong>
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.disabled">{timeAgo(activity.createdAt || activity.timestamp)}</Typography>
                }
              />
            </ListItem>
            {idx < Math.min(activities.length, maxItems) - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default ActivityFeed;
